import {
  stagger,
  useAnimate,
  useInView,
  useReducedMotion,
} from 'framer-motion';
import { useTranslation } from 'next-i18next/pages';
import React, { useEffect } from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import TransCustom from '@/components/apps/ui/transCustom/TransCustom';
import resolveMotionEase from '@/components/shared/animations/motion/resolveMotionEase';

import { SoundAnalyticsCard } from './SoundAnalyticsCard';

const whiteCards = [
  {
    imgUrl: '/images/apps/home/sec_03_img_01.svg',
    title: '공공장소 위험 감지 이상음원 인식 기술',
    description:
      '소동, 신음, 비명 등 비언어 음성을 포착하는 기술로 긴급한 위험 상황을 빠르고 폭넓게 인식할 수 있어요.',
  },
  {
    imgUrl: '/images/apps/home/sec_03_img_02.svg',
    title: '다양한 환경음 분석 능력',
    description:
      '폭발음, 총소리, 자동차 사고 등 다양한 환경음을 구분할 수 있어요.',
  },
  {
    imgUrl: '/images/apps/home/sec_03_img_03.svg',
    title: '음성 키워드 검출 기술',
    description:
      '도와주세요, 살려주세요 등 4가지 이상의 키워드를 감지하는 AI 모델로 도움의 목소리를 정확하게 포착해요.',
  },
  {
    imgUrl: '/images/apps/home/sec_03_img_05.svg',
    title: '혁신제품 인증',
    description:
      '공공성과 기술 혁신성을 인정받은 혁신제품 인증으로 현장 도입 가능성을 넓혀가고 있어요.',
  },
];

/**
 * ### SoundAnalytics
 *
 * Presents the technical evidence cards that support the site's sound-AI
 * claims: the publication record on the left, and on the right a two-by-two
 * of what the models can hear, closed by the innovation certification.
 *
 * @returns The rendered technical-strength section
 */
export default function SoundAnalytics() {
  const { t } = useTranslation('common');
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(scope, {
    once: true,
    margin: '0px 0px -10% 0px',
  });

  useEffect(() => {
    const initialControls = animate(
      '.card',
      { opacity: 0, y: 30 },
      { duration: 0 },
    );
    if (!isInView) return () => initialControls.stop();

    const controls = animate(
      '.card',
      { opacity: 1, y: 0 },
      {
        delay: shouldReduceMotion ? 0 : stagger(0.1),
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: resolveMotionEase('power1.out'),
      },
    );

    return () => {
      initialControls.stop();
      controls.stop();
    };
  }, [animate, isInView, shouldReduceMotion]);

  return (
    // 원본은 `padding: 100px 0` 단축 속성이라 좌우 0 을 px-0 으로 명시한다.
    <div className="bg-[linear-gradient(180deg,#fff_0%,#f6f6f7_10%)] px-0 py-[100px] lg:py-[clamp(100px,16.667vw,200px)]">
      <Area>
        <SectionTitle
          title={t('세계 최고 수준의 실시간 사운드 분석 AI')}
          description={
            <TransCustom
              className="height-150"
              i18nKey="디플리는 자체 인공지능 모델 개발과 기술 향상에 노력을 기울이고 있어요. 다양한 국내외 인증으로 검증된 세계 최고 수준의 기술력을 소개합니다."
            />
          }
        />
        <div
          ref={scope}
          className="mt-[40px] grid gap-[16px] lg:mt-[clamp(40px,6.667vw,80px)] lg:grid-cols-2"
        >
          <SoundAnalyticsCard
            variant="black"
            title={
              <TransCustom i18nKey="글로벌 1위 신호처리 학회 ICASSP 2024-2026 연속 논문 게재" />
            }
            description={
              <TransCustom i18nKey="다양한 마이크에서 음원으로 상황을 구분하는 알고리즘에 대해 세계 최고 (SOTA) 기술을 보유하고 있어요." />
            }
            className="card"
          />
          <div className="grid gap-[16px] lg:grid-cols-2">
            {whiteCards.map((card) => (
              <SoundAnalyticsCard
                key={card.title}
                variant="white"
                imgUrl={card.imgUrl}
                title={<TransCustom i18nKey={card.title} />}
                description={<TransCustom i18nKey={card.description} />}
                className="card"
              />
            ))}
          </div>
        </div>
      </Area>
    </div>
  );
}
